import { gql } from "lib/apolloClient";
import {
  gradesItemFragment,
  basicStaffDetailsFragment,
} from "./ScoreCategoriesFragments";

export const getSubjectBasedCoursesQuery = gql`
  query getSubjectBasedCourses($id: ID!, $filters: CourseFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(filters: $filters) {
          edges {
            node {
              id
              title
              grades {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const getSubjectDetailsQuery = gql`
  query getSubjectsForLearningStandards(
    $organizationId: ID!
    $filters: SubjectFilterInput
    $pointAssessmentFilters: SubjectPointAssessmentSetFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $filters) {
          id
          name
          type
          icon
          isArchived
          ibOfferedSubject {
            id
          }
          pointAssessmentSets(filters: $pointAssessmentFilters) {
            totalCount
            edges {
              node {
                id
                associatedEntities {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationPointSetQuery = gql`
  query getOrganizationPointSet($id: ID!, $filters: PointAssessmentSetFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        pointAssessmentSets(filters: $filters) {
          totalCount
          edges {
            node {
              id
              label
              type
              subType
              isShowGradeBoundaries
              associatedEntities {
                id
                ... on Course {
                  id
                  title
                }
                ... on Subject {
                  id
                  name
                }
                ... on Grade {
                  ...gradeItem
                }

                ... on AcademicTerm {
                  id
                  label
                }
              }
              categories {
                id
                label
                colorCode
                percentageWeight
                hlWeight
                slWeight
                values {
                  id
                  value
                  minValue
                  maxValue
                  genericTag {
                    id
                    label
                  }
                  valueType
                  weightageType
                }
                gradeBoundary {
                  id
                  categories {
                    id
                    label
                    displaySequence
                  }
                }
                gradeScaleType
                displaySequence
                overridenCategoryScoresCount
              }
              updatedAt
              updatedBy {
                ...basicStaffDetails
              }
              createdBy {
                ...basicStaffDetails
              }
              metaData
            }
          }
        }
      }
    }
  }
  ${basicStaffDetailsFragment}
  ${gradesItemFragment.gradeFragment}
`;

export const getAssignmentsWithAutoEvaluateToolQuery = gql`
  query getAssignmentsWithAutoEvaluateTool($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        assignments(
          filters: {
            assessmentToolTypes: [
              ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY
            ]
          }
        ) {
          totalCount
          edges {
            node {
              id
              course {
                id
              }
              content {
                id
                ... on Assessment {
                  id
                  title {
                    id
                    value
                  }
                  image {
                    id
                    value
                  }
                  assessmentType {
                    id
                    value
                  }
                  assessmentTools(
                    filters: {
                      assessmentToolType: ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY
                    }
                  ) {
                    edges {
                      node {
                        id
                        assessmentToolItem {
                          id
                          ... on AcademicCriteriaSet {
                            id
                          }
                        }
                      }
                    }
                  }
                }
              }
              contentType
            }
          }
        }
      }
    }
  }
`;

export const getAssessmentsMappedToCategorySetQuery = gql`
  query getAssessmentsMappedToACategorySet($id: ID!) {
    node(id: $id, type: ACADEMIC_CRITERIA_SET) {
      id
      ... on AcademicCriteriaSet {
        academicCriteriaValues {
          assignments {
            id
            content {
              ... on Assessment {
                id
                title {
                  id
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAssessmentsMappedToCategoryQuery = gql`
  query getAssessmentsMappedToACategory($id: ID!) {
    node(id: $id, type: ACADEMIC_CRITERIA_VALUE) {
      id
      ... on AcademicCriteriaValue {
        assignments {
          id
          content {
            ... on Assessment {
              id
              title {
                id
                value
              }
            }
          }
        }
      }
    }
  }
`;

export const getSubjectGroupsQuery = gql`
  query getSubjectGroupsQuery(
    $id: ID!
    $filters: SubjectGroupFilterInput
    $subjectFilters: SubjectFilterInput
    $flatSubjectVariantFilters: FlatSubjectVariantFilterInput
    $flatVariantsCourseFilters: FlatVariantsCourseFilters
    $subjectCourseFilter: SubjectCourseFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        subjectGroups(filters: $filters) {
          id
          name
          type
          globalSubjectGroup {
            id
          }
          subjects(filters: $subjectFilters) {
            id
            name
            type
            icon
            iconType
            isArchived
            ibOfferedSubject {
              id
              contentSubject {
                id
                assessmentYearTitle
              }
            }
            flatVariants(filters: $flatSubjectVariantFilters) {
              level {
                id
                label
                globalTag {
                  id
                }
              }
              responseLanguage {
                id
                label
                globalTag {
                  id
                }
              }
              courses(filters: $flatVariantsCourseFilters) {
                totalCount
              }
              unitPlans {
                totalCount
              }
              isArchived
            }
            courses(filters: $subjectCourseFilter) {
              totalCount
            }
            unitPlans {
              totalCount
            }
          }
        }
      }
    }
  }
`;
